import { useQuery } from '@apollo/client';

import { getBasicUserInfo } from './queries';
import { isEmpty, isValidNumber } from './validationUtils';

import { isEmpty as lodashIsEmpty } from 'lodash';
import { GenericLines, LineCategory } from 'src/enums';

export function removeObjectAtIndex<T>(array: T[], index: number): T[] {
  if (index < 0 || index >= array.length) {
    return array;
  }

  return array.filter((_, idx) => idx !== index);
}

export function convertToTitleCase(input: string): string {
  if (isEmpty(input)) {
    return '';
  }

  // Replace underscores with spaces and split into words
  let words = input.replace(/_/g, ' ').split(' ');

  // Convert each word to lowercase except the first character
  words = words.map((word) => {
    if (word.length === 0) return ''; // Handle empty strings

    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });

  // Join the words into a single string
  return words.join(' ');
}

export function updateFormField<T, K extends keyof T>(
  obj: T,
  key: K,
  value: T[K]
): T {
  return {
    ...obj,
    [key]: value
  };
}

export type ValueOf<T> = T[keyof T];
export type KeyOf<T> = keyof T;

export function sumArray(numbers: number[]): number {
  return numbers.reduce((total, num) => total + num, 0);
}

export function useUserFullNameById(
  userId: string,
  includeMiddleName = false
): string | null {
  const { data } = useQuery(getBasicUserInfo, {
    variables: { id: userId },
    skip: !userId,
    fetchPolicy: 'no-cache'
  });

  const user = data?.core?.getUserDetails?.user;

  return user
    ? includeMiddleName
      ? `${user.firstName || ''} ${user.middleName || ''}  ${user.lastName || ''}`.trim()
      : `${user.firstName || ''} ${user.lastName || ''}`.trim()
    : null;
}

/**
 *
 * @param value any
 * @returns checks if the value is empty and returns null if it is, otherwise returns the value
 */
export function valueOrNull(value: any): any {
  return lodashIsEmpty(value) ? null : value;
}

export function numberOrNull(value: any): number | null {
  return isValidNumber(value) ? parseFloat(value) : null;
}

export function compareStrings(
  a: string,
  b: string,
  caseSensitive = false
): number {
  if (!caseSensitive) {
    a = a.toLowerCase();
    b = b.toLowerCase();
  }

  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }

  return 0;
}

const lineCategories: Record<string, LineCategory> = {
  '4': LineCategory.Motor,
  '50': LineCategory.Motor,
  '51': LineCategory.Motor,
  '19': LineCategory.Medical,
  '48': LineCategory.Travel,
  '43': LineCategory.Expat
};

const genericLines: Record<string, GenericLines> = {
  '12': GenericLines.BBB,
  '3': GenericLines.MARINE,
  '20': GenericLines.MARINE_HULL,
  '33': GenericLines.MARINE_HULL,
  '6': GenericLines.MEDICAL_GROUP,
  '15': GenericLines.PLATE_GLASS
};

export function getLineCategory(line: string): LineCategory {
  return lineCategories[line] || LineCategory.Generic;
}

export function getGenericLine(line: string): GenericLines {
  return genericLines[line] || GenericLines.GENERIC;
}

export function hasRisk(line: string) {
  return [
    '5',
    '9',
    '10',
    '13',
    '21',
    '35',
    '11',
    '15',
    '16',
    '25',
    '27',
    '29',
    '30',
    '31',
    '37',
    '38',
    '39',
    '42',
    '46'
  ].includes(line);
}

export function hasProperty(line: string) {
  return ['2', '8', '23', '26'].includes(line);
}

export function hasMedical(line: string) {
  return ['6', '19', '48'].includes(line);
}

export function hasIndividualType(line: string) {
  return ['19', '48'].includes(line);
}

export function hasBeneficiaries(line: string) {
  return ['6', '19', '37', '43', '48', '51'].includes(line);
}

export function hasCertificate(line: string) {
  return ['3', '4', '51'].includes(line);
}

export const extractMessage = (msg: string): string => {
  const match = msg.match(/Message:\s*(.*?)(,|$)/);

  return match ? match[1].trim() : msg;
};
